import { logger } from '@thoughtspot/client-state-util/esm/utils';
import { getIntervalMillisForTimeBucket, getNextDateTimeEpochFunction, isAboveHourlyBucket, isAboveWeeklyBucket, timeBucketCategories, } from '@thoughtspot/date-format-util';
import _ from 'lodash';
import { applyConditionalFormattingStyle, getXRawValuesFromSerie, } from '../../../highcharts/highchart-series-util';
import { HandleMissingValues } from '../../../types';
function resetDate(timestamp, dayOfMonth) {
    return new Date(timestamp).setDate(dayOfMonth);
}
function resetTime(timestamp, dateWithTime) {
    const time = new Date(dateWithTime);
    return new Date(timestamp).setHours(time.getHours(), time.getMinutes(), time.getSeconds(), time.getMilliseconds());
}
function getNextPointAfterMin(initialPoint, dataMin, incrementMethod) {
    let startPoint = initialPoint;
    for (; startPoint <= dataMin;) {
        startPoint = incrementMethod(startPoint);
    }
    return startPoint;
}
function resetDataBasedOnBucket(processedXData, timeBucketCategory, dataMin) {
    if (isAboveWeeklyBucket(timeBucketCategory)) {
        for (let ind = 0; ind < processedXData.length; ind++) {
            const d = processedXData[ind];
            processedXData[ind] = resetDate(d, 1);
        }
    }
    if (isAboveHourlyBucket(timeBucketCategory)) {
        for (let ind = 0; ind < processedXData.length; ind++) {
            const d = processedXData[ind];
            processedXData[ind] = resetTime(d, dataMin);
        }
    }
    return processedXData;
}
function generateBasePoints(config) {
    const { dataMin, dataMax, timeBucketCategory, shouldResetTime } = config;
    let { startPoint } = config;
    const maxPointRange = getIntervalMillisForTimeBucket(timeBucketCategories.HOUR);
    const maxPossiblePoints = Math.ceil((dataMax - dataMin) / maxPointRange);
    const tickPoints = new Array(maxPossiblePoints);
    const incrementMethod = getNextDateTimeEpochFunction(timeBucketCategory);
    let arrInd = 0;
    for (; startPoint < dataMax;) {
        tickPoints[arrInd++] = startPoint;
        startPoint = incrementMethod(startPoint);
        if (shouldResetTime) {
            startPoint = resetTime(startPoint, dataMin);
        }
    }
    return _.without(tickPoints, undefined);
}
function generatePointsForAboveWeeklyBucket(config) {
    const incrementMethod = getNextDateTimeEpochFunction(config.timeBucketCategory);
    const startPoint = getNextPointAfterMin(resetDate(config.dataMin, 1), config.dataMin, incrementMethod);
    return generateBasePoints({
        ...config,
        startPoint,
        shouldResetTime: true,
    });
}
function generatePointsForAboveHourlyBucket(config) {
    if (isAboveWeeklyBucket(config.timeBucketCategory)) {
        return generatePointsForAboveWeeklyBucket(config);
    }
    return generateBasePoints({
        ...config,
        startPoint: config.dataMin,
        shouldResetTime: true,
    });
}
function generatePointsForTimelyBucket(config) {
    return generateBasePoints({
        ...config,
        startPoint: config.dataMin,
        shouldResetTime: false,
    });
}
function generateAllPointsForTimeSeriesData(timeBucketCategory, dataMin, dataMax) {
    const generator = isAboveHourlyBucket(timeBucketCategory)
        ? generatePointsForAboveHourlyBucket
        : generatePointsForTimelyBucket;
    return generator({
        dataMin,
        dataMax,
        timeBucketCategory,
    });
}
function addNewPointsToSeries(serie, missingTickPoints, handleMissingValues) {
    if (_.isEmpty(missingTickPoints)) {
        return;
    }
    for (let i = 0; i < missingTickPoints.length; i++) {
        const tick = missingTickPoints[i];
        let point;
        if (handleMissingValues === HandleMissingValues.ShowGap) {
            point = {
                x: tick,
                y: null,
                xRawValues: [tick],
                yRawValues: null,
            };
        }
        else {
            point = {
                x: tick,
                y: 0,
                xRawValues: [tick],
                yRawValues: [0],
            };
        }
        serie.addPoint(point, false);
    }
}
function handleMissingValues() {
    const chart = this;
    if (_.isNil(chart.xAxis) || _.isEmpty(chart.xAxis)) {
        return this;
    }
    if (_.isNil(chart.xAxis[0].series) || _.isEmpty(chart.xAxis[0].series)) {
        return this;
    }
    try {
        chart.xAxis[0].series.forEach(serie => {
            const { handleMissingValues, isTimeSeries, timeBucketCategory, } = serie.userOptions;
            if (!handleMissingValues)
                return;
            if (handleMissingValues === HandleMissingValues.DontShow) {
                return;
            }
            if (!isTimeSeries) {
                logger.warn('handle null/missing values not supported in non time series data.');
                return;
            }
            const tickPoints = generateAllPointsForTimeSeriesData(timeBucketCategory, serie.xAxis.dataMin, serie.xAxis.dataMax);
            const rawValues = getXRawValuesFromSerie(serie, true, true);
            const processedRawValues = resetDataBasedOnBucket(rawValues, timeBucketCategory, serie.xAxis.dataMin);
            const processedTickPoints = resetDataBasedOnBucket(tickPoints, timeBucketCategory, serie.xAxis.dataMin);
            const missingTickPoints = _.difference(processedTickPoints, processedRawValues);
            addNewPointsToSeries(serie, missingTickPoints, handleMissingValues);
        });
    }
    catch (e) {
        logger.error(e, 'unable to handle missing values in highchartsOnloadCustomHandler.');
    }
    return chart;
}
export function highchartsOnloadCustomHandler(H) {
    H.wrap(H.Chart.prototype, 'onload', function (proceed) {
        var _a, _b;
        const chart = this;
        if ((_a = chart === null || chart === void 0 ? void 0 : chart.userOptions) === null || _a === void 0 ? void 0 : _a.isHandleMissingValuesEnabled) {
            handleMissingValues.call(chart);
            (_b = chart.userOptions.series) === null || _b === void 0 ? void 0 : _b.forEach(serie => {
                applyConditionalFormattingStyle(serie, chart.userOptions.series);
            });
        }
        return proceed.apply(chart, Array.prototype.slice.call(arguments, 1));
    });
}
//# sourceMappingURL=highcharts-onload-custom-handler.js.map